/**
 * Document formatting utilities
 * Provides consistent formatting for Brazilian documents (CPF, CNPJ, etc.)
 */

/**
 * Formats a CNPJ (Brazilian company registration number)
 * @param cnpj - CNPJ string (with or without formatting)
 * @returns Formatted CNPJ (00.000.000/0000-00) or '-' if invalid
 * @example
 * formatCNPJ('12345678901234') // Returns '12.345.678/9012-34'
 * formatCNPJ('12.345.678/9012-34') // Returns '12.345.678/9012-34'
 * formatCNPJ(null) // Returns '-'
 */
export const formatCNPJ = (cnpj?: string | null): string => {
  if (!cnpj) return '-';

  // Remove all non-digit characters
  const cleanCNPJ = cnpj.replace(/\D/g, '');

  // Check if it has the correct length for CNPJ
  if (cleanCNPJ.length !== 14) return cnpj;

  // Apply CNPJ formatting: 00.000.000/0000-00
  return cleanCNPJ.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
};

/**
 * Formats a CPF (Brazilian individual taxpayer registry)
 * @param cpf - CPF string (with or without formatting)
 * @returns Formatted CPF (000.000.000-00) or '-' if invalid
 * @example
 * formatCPF('12345678901') // Returns '123.456.789-01'
 * formatCPF('123.456.789-01') // Returns '123.456.789-01'
 * formatCPF(null) // Returns '-'
 */
export const formatCPF = (cpf?: string | null): string => {
  if (!cpf) return '-';

  // Remove all non-digit characters
  const cleanCPF = cpf.replace(/\D/g, '');

  // Check if it has the correct length for CPF
  if (cleanCPF.length !== 11) return cpf;

  // Apply CPF formatting: 000.000.000-00
  return cleanCPF.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

/**
 * Formats a document (auto-detects CPF or CNPJ based on length)
 * @param document - Document string (with or without formatting)
 * @returns Formatted document or '-' if invalid
 * @example
 * formatDocument('12345678901234') // Returns '12.345.678/9012-34' (CNPJ)
 * formatDocument('12345678901') // Returns '123.456.789-01' (CPF)
 */
export const formatDocument = (document?: string | null): string => {
  if (!document) return '-';

  // Remove all non-digit characters
  const cleanDocument = document.replace(/\D/g, '');

  // Check document type based on length
  if (cleanDocument.length === 11) {
    return formatCPF(cleanDocument);
  } else if (cleanDocument.length === 14) {
    return formatCNPJ(cleanDocument);
  }

  // Return original if length doesn't match CPF or CNPJ
  return document;
};

/**
 * Formats a CEP (Brazilian postal code)
 * @param cep - CEP string (with or without formatting)
 * @returns Formatted CEP (00000-000) or '-' if invalid
 * @example
 * formatCEP('12345678') // Returns '12345-678'
 * formatCEP('12345-678') // Returns '12345-678'
 * formatCEP(null) // Returns '-'
 */
export const formatCEP = (cep?: string | null): string => {
  if (!cep) return '-';

  // Remove all non-digit characters
  const cleanCEP = cep.replace(/\D/g, '');

  // Check if it has the correct length for CEP
  if (cleanCEP.length !== 8) return cep;

  // Apply CEP formatting: 00000-000
  return cleanCEP.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

/**
 * Validates a CNPJ
 * @param cnpj - CNPJ string to validate
 * @returns true if valid, false otherwise
 */
export const isValidCNPJ = (cnpj?: string | null): boolean => {
  if (!cnpj) return false;

  const cleanCNPJ = cnpj.replace(/\D/g, '');

  if (cleanCNPJ.length !== 14) return false;

  // Check if all digits are the same
  if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;

  // Validate check digits
  let size = cleanCNPJ.length - 2;
  let numbers = cleanCNPJ.substring(0, size);
  const digits = cleanCNPJ.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  size = size + 1;
  numbers = cleanCNPJ.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
};

/**
 * Validates a CPF
 * @param cpf - CPF string to validate
 * @returns true if valid, false otherwise
 */
export const isValidCPF = (cpf?: string | null): boolean => {
  if (!cpf) return false;

  const cleanCPF = cpf.replace(/\D/g, '');

  if (cleanCPF.length !== 11) return false;

  // Check if all digits are the same
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;

  // Validate check digits
  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleanCPF.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleanCPF.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(10, 11))) return false;

  return true;
};

/**
 * Removes formatting from a document string (returns only digits)
 * @param document - Formatted document string
 * @returns Clean document with only digits
 * @example
 * cleanDocument('12.345.678/9012-34') // Returns '12345678901234'
 * cleanDocument('123.456.789-01') // Returns '12345678901'
 */
export const cleanDocument = (document?: string | null): string => {
  if (!document) return '';
  return document.replace(/\D/g, '');
};
